import {
  UsageExplorerLocationRowDto,
  UsageExplorerManagerRowDto,
} from "../../types/analytics/AnalyticsPageDataDto";
import { UsageExplorerLocationAggregate } from "../../types/analytics/UsageExplorerLocationAggregate";
import { UsageAttentionCardId } from "../../types/analytics/UsageExplorerViewToken";

export function filterUsageExplorerManagerRows(
  rows: UsageExplorerManagerRowDto[],
  attentionCardId: UsageAttentionCardId | undefined,
  searchText: string,
) {
  const search = searchText.trim().toLocaleLowerCase();

  return rows.filter(
    (row) =>
      matchesAttentionCard(row, attentionCardId) &&
      (!search || row.managerName?.toLocaleLowerCase().includes(search)),
  );
}

export function buildUsageExplorerLocationLevel(
  rows: UsageExplorerLocationRowDto[],
  breadcrumbIds: number[],
  attentionCardId: UsageAttentionCardId | undefined,
  searchText: string,
): UsageExplorerLocationAggregate[] {
  const depth = breadcrumbIds.length;
  const search = searchText.trim().toLocaleLowerCase();
  const groupedRows = new Map<string, UsageExplorerLocationAggregate>();

  rows
    .filter((row) => matchesAttentionCard(row, attentionCardId))
    .filter((row) =>
      breadcrumbIds.every(
        (locationId, index) =>
          row.breadcrumb[index]?.locationId === locationId,
      ),
    )
    .forEach((row) => {
      const node = row.breadcrumb[depth];
      const currentLevelNode =
        !node && depth > 0 && row.breadcrumb.length === depth
          ? row.breadcrumb[depth - 1]
          : undefined;
      if (!node && !currentLevelNode && (depth > 0 || row.locationId !== null)) {
        return;
      }

      const key = node
        ? node.locationId.toString()
        : currentLevelNode
        ? `current-${currentLevelNode.locationId}`
        : "unassigned";
      const current = groupedRows.get(key) ?? {
        locationId: node?.locationId ?? currentLevelNode?.locationId ?? null,
        locationName:
          node?.locationName ?? currentLevelNode?.locationName ?? null,
        assignedCount: 0,
        completedCount: 0,
        outstandingCount: 0,
        notStartedCount: 0,
        waitingOnManagerStalledCount: 0,
        oldestOutstandingAgeDays: null,
        totalOutstandingWaitDays: 0,
        hasChildren: false,
        isCurrentLocation: !!currentLevelNode,
      };

      current.assignedCount += row.assignedCount;
      current.completedCount += row.completedCount;
      current.outstandingCount += row.outstandingCount;
      current.notStartedCount += row.notStartedCount;
      current.waitingOnManagerStalledCount +=
        row.waitingOnManagerStalledCount;
      current.oldestOutstandingAgeDays = maxNullable(
        current.oldestOutstandingAgeDays,
        row.oldestOutstandingAgeDays,
      );
      current.totalOutstandingWaitDays += row.totalOutstandingWaitDays;
      current.hasChildren =
        current.hasChildren || row.breadcrumb.length > depth + 1;
      groupedRows.set(key, current);
    });

  return Array.from(groupedRows.values())
    .filter(
      (row) =>
        !search ||
        (row.locationName ?? "").toLocaleLowerCase().includes(search),
    )
    .sort(
      (left, right) =>
        right.totalOutstandingWaitDays - left.totalOutstandingWaitDays ||
        right.outstandingCount - left.outstandingCount ||
        (left.locationName ?? "").localeCompare(right.locationName ?? ""),
    );
}

function matchesAttentionCard(
  row: {
    notStartedCount: number;
    waitingOnManagerStalledCount: number;
  },
  attentionCardId: UsageAttentionCardId | undefined,
) {
  if (attentionCardId === "EMPLOYEES_NOT_STARTED") {
    return row.notStartedCount > 0;
  }

  if (attentionCardId === "WAITING_ON_MANAGER_STALLED") {
    return row.waitingOnManagerStalledCount > 0;
  }

  return true;
}

function maxNullable(left: number | null, right: number | null) {
  if (left === null) return right;
  if (right === null) return left;
  return Math.max(left, right);
}
